import { App } from '@capacitor/app';
import { detectPlatform, type Platform } from './platform';

/** Telas de onde o "voltar" sai do app (não há tela anterior a elas). */
const EXIT_PATHS = ['/entrar', '/visao-geral', '/empresas', '/sem-acesso'];

/** Diálogo, folha ou menu aberto (Radix marca com data-state="open") ou lista de sugestões aberta (busca, menções). */
const OPEN_OVERLAY = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="combobox"][aria-expanded="true"]',
].join(', ');

export type BackAction = 'close-overlay' | 'back' | 'exit';

/**
 * O que o botão "voltar" do Android faz (T-231): fecha o diálogo ou menu aberto; senão volta à tela anterior; nas
 * telas iniciais (ou sem histórico) sai do app — como nos apps nativos.
 */
export function decideBack(state: {
  overlayOpen: boolean;
  pathname: string;
  canGoBack: boolean;
}): BackAction {
  if (state.overlayOpen) return 'close-overlay';
  if (!state.canGoBack || EXIT_PATHS.includes(state.pathname)) return 'exit';
  return 'back';
}

/** Liga o botão "voltar" do Android ao app (no navegador e no desktop não faz nada). */
export async function installAndroidBackButton(
  router: { navigate(delta: number): unknown; state: { location: { pathname: string } } },
  platform: Platform = detectPlatform(),
): Promise<(() => void) | null> {
  if (platform !== 'android') return null;
  const listener = await App.addListener('backButton', ({ canGoBack }) => {
    const action = decideBack({
      overlayOpen: document.querySelector(OPEN_OVERLAY) !== null,
      pathname: router.state.location.pathname,
      canGoBack,
    });
    if (action === 'close-overlay') {
      // O mesmo que apertar Esc: o diálogo/menu fecha e devolve o foco a quem o abriu.
      const target = document.activeElement ?? document.body;
      target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }));
    } else if (action === 'back') {
      void router.navigate(-1);
    } else {
      void App.exitApp();
    }
  });
  return () => void listener.remove();
}
